/**
 * Admin Templates Page - Full Implementation Matching Figma
 */

import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { 
  Search, 
  Plus, 
  MoreVertical,
  Edit2,
  Copy,
  FileText,
  Send,
  CheckCircle,
  Archive,
  ArchiveRestore,
  Star,
  Clock,
  GitBranch,
  Eye,
  SlidersHorizontal,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { 
  useTemplates, 
  useDeleteTemplate,
  useRestoreTemplate,
  useCreateTemplate,
  useSubmitForReview,
  usePublishTemplate,
  useDeprecateTemplate,
  useSetDefaultTemplate,
  type Template,
} from '@/hooks/use-templates';
import { 
  TemplateStatus, 
  TemplateType,
  TEMPLATE_STATUS_LABELS,
  TEMPLATE_TYPE_LABELS,
  TEMPLATE_CATEGORY_LABELS,
  getStatusColor,
  type TemplateCategory,
} from '@audra-flow/types';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/hooks/use-toast';
import { TemplateIcon } from '@/components/ui/template-icon';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { VersionHistoryModal } from '@/components/templates/version-history-modal';

// Status badge component - matching Figma design
function StatusBadge({ status }: { status: string }) {
  const colors = getStatusColor(status as TemplateStatus);
  const label = TEMPLATE_STATUS_LABELS[status as TemplateStatus] || status;
  
  const icons: Record<string, React.ReactNode> = {
    ACTIVE: <CheckCircle className="w-3.5 h-3.5" />,
    DRAFT: <Edit2 className="w-3.5 h-3.5" />,
    IN_REVIEW: <Clock className="w-3.5 h-3.5" />,
    DEPRECATED: <Archive className="w-3.5 h-3.5" />,
  };

  return (
    <span className={cn(
      'inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-md border',
      colors.bg,
      colors.text,
      colors.border
    )}>
      {icons[status]}
      {label}
    </span>
  );
}

export default function AdminTemplatesPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [typeFilter, setTypeFilter] = useState<string>('');
  const [showFilters, setShowFilters] = useState(false);
  
  // Persist showArchived in URL so it's maintained when navigating back
  const showArchived = searchParams.get('showArchived') === 'true';
  const setShowArchived = (value: boolean) => {
    const newParams = new URLSearchParams(searchParams);
    if (value) {
      newParams.set('showArchived', 'true');
    } else {
      newParams.delete('showArchived');
    }
    setSearchParams(newParams, { replace: true });
  };
  
  // Save current list state to sessionStorage for preserving when navigating back from template edit
  useEffect(() => {
    // Only save relevant state params (exclude highlight which is transient)
    const stateParams = new URLSearchParams();
    if (showArchived) stateParams.set('showArchived', 'true');
    sessionStorage.setItem('templatesListState', stateParams.toString());
  }, [showArchived]);
  
  const [sortBy, setSortBy] = useState<'name' | 'type' | 'status' | 'lastUpdated' | 'owner'>('lastUpdated');
  
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  
  // Pending highlight for newly created/updated template
  const [pendingHighlight, setPendingHighlight] = useState<string | null>(null);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  
  // Confirmation modal state
  const [deleteModal, setDeleteModal] = useState<{ open: boolean; template: Template | null }>({ open: false, template: null });
  const [deprecateModal, setDeprecateModal] = useState<{ open: boolean; template: Template | null }>({ open: false, template: null });
  
  // Version history modal state
  const [historyModal, setHistoryModal] = useState<{ open: boolean; template: Template | null }>({ open: false, template: null });
  
  // Menu position state (for opening menu upward when near bottom)
  const [menuPosition, setMenuPosition] = useState<'below' | 'above'>('below');
  const [menuCoords, setMenuCoords] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  
  const { toast } = useToast();

  const { data, isLoading, isError, refetch } = useTemplates({
    search: searchQuery || undefined,
    category: categoryFilter || undefined,
    status: statusFilter || undefined,
    showArchived: showArchived,
    type: typeFilter || undefined,
  });

  const createTemplate = useCreateTemplate();

  const deleteTemplate = useDeleteTemplate();

  // Sort templates client-side to match Figma behavior
  // Use spread to create a new array (avoid mutating the original)
  // Use stable sort with ID as tie-breaker to prevent order shuffling
  const templates = [...(data?.templates || [])].sort((a, b) => {
    let comparison = 0;
    switch (sortBy) {
      case 'name':
        comparison = a.name.localeCompare(b.name);
        break;
      case 'type':
        comparison = (a.type || '').localeCompare(b.type || '');
        break;
      case 'status':
        comparison = (a.status || '').localeCompare(b.status || '');
        break;
      case 'lastUpdated':
        comparison = new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime();
        break;
      case 'owner':
        comparison = (a.owner?.name || '').localeCompare(b.owner?.name || '');
        break;
    }
    // Apply sort direction
    const result = sortOrder === 'asc' ? comparison : -comparison;
    // Stable sort: if primary comparison is equal, use ID as tie-breaker
    if (result === 0) {
      return a.id.localeCompare(b.id);
    }
    return result;
  });

  // Check for highlight parameter on mount
  useEffect(() => {
    const highlight = searchParams.get('highlight');
    if (highlight) {
      setPendingHighlight(highlight);
      // Remove highlight param from URL
      searchParams.delete('highlight');
      setSearchParams(searchParams, { replace: true });
      // Force refetch to ensure we have fresh data with the new template
      refetch();
    }
  }, [searchParams, setSearchParams, refetch]);

  // Apply highlight once templates are loaded and contain the pending highlight
  // Use direct DOM manipulation to avoid animation restart on React re-renders
  useEffect(() => {
    if (pendingHighlight && templates.length > 0) {
      const templateExists = templates.some(t => t.id === pendingHighlight);
      if (templateExists) {
        const highlightId = pendingHighlight;
        setPendingHighlight(null);
        
        // Use requestAnimationFrame to ensure the DOM is ready
        requestAnimationFrame(() => {
          const row = document.querySelector(`[data-template-id="${highlightId}"]`) as HTMLElement;
          if (row) {
            // Scroll into view
            row.scrollIntoView({ behavior: 'smooth', block: 'center' });
            
            // Apply animation directly to DOM (won't restart on React re-renders)
            row.style.animation = 'highlight-success-pulse 0.8s ease-in-out 3, highlight-success-fade 0.6s ease-out 2.4s forwards';
            
            // Remove animation style after it completes
            setTimeout(() => {
              row.style.animation = '';
            }, 3200);
          }
        });
      } else {
        setPendingHighlight(null);
      }
    }
  }, [pendingHighlight, templates]);

  const handleSort = (column: typeof sortBy) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  const getSortIcon = (column: typeof sortBy) => {
    if (sortBy !== column) return '↕';
    return sortOrder === 'asc' ? '↑' : '↓';
  };

  const handleRowClick = (templateId: string) => {
    navigate(`/admin/templates/${templateId}`);
  };

  // Close menu when clicking outside
  const handleClickOutside = () => {
    if (openMenuId) setOpenMenuId(null);
  };

  // Open delete confirmation modal
  const handleDeleteClick = (template: Template) => {
    setDeleteModal({ open: true, template });
    setOpenMenuId(null);
  };

  // Confirm delete action
  const confirmDelete = async () => {
    if (!deleteModal.template) return;
    
    try {
      await deleteTemplate.mutateAsync(deleteModal.template.id);
      toast({
        title: 'Template archived',
        description: `"${deleteModal.template.name}" has been moved to archive.`,
      });
      setDeleteModal({ open: false, template: null });
      // Note: useDeleteTemplate already invalidates the query, no need for manual refetch
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to archive template. Please try again.',
        variant: 'destructive',
      });
    }
  };

  // Open deprecate confirmation modal
  const handleDeprecateClick = (template: Template) => {
    setDeprecateModal({ open: true, template });
    setOpenMenuId(null);
  };

  // Confirm deprecate action
  const confirmDeprecate = async (deprecateTemplateMutation: ReturnType<typeof useDeprecateTemplate>) => {
    if (!deprecateModal.template) return;
    
    try {
      await deprecateTemplateMutation.mutateAsync();
      toast({
        title: 'Template deprecated',
        description: `"${deprecateModal.template.name}" has been deprecated.`,
      });
      setDeprecateModal({ open: false, template: null });
      // Note: useDeprecateTemplate already invalidates the query
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to deprecate template.',
        variant: 'destructive',
      });
    }
  };

  // Action handlers component
  const ActionHandlers = ({ template }: { template: Template }) => {
    const submitForReview = useSubmitForReview(template.id);
    const publishTemplate = usePublishTemplate(template.id);
    const deprecateTemplate = useDeprecateTemplate(template.id);
    const setDefaultTemplate = useSetDefaultTemplate(template.id);
    const restoreTemplate = useRestoreTemplate();

    const handleSubmitForReview = async () => {
      try {
        await submitForReview.mutateAsync();
        toast({
          title: 'Submitted for review',
          description: `"${template.name}" has been submitted for review.`,
        });
        setOpenMenuId(null);
      } catch (error) {
        toast({
          title: 'Error',
          description: error instanceof Error ? error.message : 'Failed to submit for review.',
          variant: 'destructive',
        });
      }
    };

    const handlePublish = async () => {
      try {
        await publishTemplate.mutateAsync();
        toast({
          title: 'Template published',
          description: `"${template.name}" is now active and available for use.`,
        });
        setOpenMenuId(null);
      } catch (error) {
        toast({
          title: 'Error',
          description: error instanceof Error ? error.message : 'Failed to publish template.',
          variant: 'destructive',
        });
      }
    };

    const handleDeprecate = () => {
      handleDeprecateClick(template);
    };

    const handleSetDefault = async () => {
      try {
        await setDefaultTemplate.mutateAsync();
        toast({
          title: 'Default template set',
          description: `"${template.name}" is now the default for ${TEMPLATE_TYPE_LABELS[template.type as TemplateType] || template.type}.`,
        });
        setOpenMenuId(null);
      } catch (error) {
        toast({
          title: 'Error',
          description: error instanceof Error ? error.message : 'Failed to set default template.',
          variant: 'destructive',
        });
      }
    };

    const handleRestore = async () => {
      try {
        await restoreTemplate.mutateAsync(template.id);
        toast({
          title: 'Template restored',
          description: `"${template.name}" has been restored from archive.`,
        });
        setOpenMenuId(null);
      } catch (error) {
        toast({
          title: 'Error',
          description: error instanceof Error ? error.message : 'Failed to restore template.',
          variant: 'destructive',
        });
      }
    };

    const menuItemClass = "w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-surface-secondary transition-colors disabled:opacity-50 disabled:cursor-not-allowed";
    const dangerItemClass = "w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed";
    const restoreItemClass = "w-full flex items-center gap-3 px-4 py-2.5 text-sm text-green-600 hover:bg-green-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed";

    return (
      <div className="py-1">
        {/* Archived template actions */}
        {template.isArchived ? (
          <>
            {/* Restore - for archived templates */}
            <button
              onClick={handleRestore}
              disabled={restoreTemplate.isPending}
              className={restoreItemClass}
            >
              <ArchiveRestore className="w-4 h-4" />
              <span>Restore</span>
            </button>
            
            {/* View */}
            <Link
              to={`/admin/templates/${template.id}`}
              className={menuItemClass}
            >
              <Eye className="w-4 h-4 text-gray-500" />
              <span>View</span>
            </Link>
            
            {/* Duplicate */}
            <button 
              className={menuItemClass}
              onClick={() => {
                setOpenMenuId(null);
                navigate(`/admin/templates/new?duplicate=${template.id}`);
              }}
            >
              <Copy className="w-4 h-4 text-gray-500" />
              <span>Duplicate</span>
            </button>
          </>
        ) : (
          <>
            {/* View / Edit */}
            <Link
              to={`/admin/templates/${template.id}`}
              className={menuItemClass}
            >
              <Eye className="w-4 h-4 text-gray-500" />
              <span>View / Edit</span>
            </Link>
            
            {/* Submit for Review - only for DRAFT */}
            {template.status === 'DRAFT' && (
              <button
                onClick={handleSubmitForReview}
                disabled={submitForReview.isPending}
                className={menuItemClass}
              >
                <Send className="w-4 h-4 text-gray-500" />
                <span>Submit for Review</span>
              </button>
            )}

            {/* Publish - only for DRAFT or IN_REVIEW */}
            {(template.status === 'DRAFT' || template.status === 'IN_REVIEW') && (
              <button
                onClick={handlePublish}
                disabled={publishTemplate.isPending}
                className={menuItemClass}
              >
                <CheckCircle className="w-4 h-4 text-green-600" />
                <span>Publish</span>
              </button>
            )}

            {/* Set as Default - only for ACTIVE and not already default */}
            {template.status === 'ACTIVE' && !template.isDefault && (
              <button
                onClick={handleSetDefault}
                disabled={setDefaultTemplate.isPending}
                className={menuItemClass}
              >
                <Star className="w-4 h-4 text-amber-500" />
                <span>Set as Default</span>
              </button>
            )}

            {/* Deprecate - only for ACTIVE */}
            {template.status === 'ACTIVE' && (
              <button
                onClick={handleDeprecate}
                disabled={deprecateTemplate.isPending}
                className={menuItemClass}
              >
                <Archive className="w-4 h-4 text-orange-500" />
                <span>Deprecate</span>
              </button>
            )}

            {/* Duplicate */}
            <button 
              className={menuItemClass}
              onClick={() => {
                setOpenMenuId(null);
                navigate(`/admin/templates/new?duplicate=${template.id}`);
              }}
            >
              <Copy className="w-4 h-4 text-gray-500" />
              <span>Duplicate</span>
            </button>
            
            {/* Archive (Delete) */}
            <div className="border-t border-gray-200 my-1" />
            <button
              onClick={() => handleDeleteClick(template)}
              disabled={deleteTemplate.isPending}
              className={dangerItemClass}
            >
              <Archive className="w-4 h-4" />
              <span>Archive</span>
            </button>
          </>
        )}
      </div>
    );
  };

  return (
    <div className="flex flex-col h-full overflow-hidden" onClick={handleClickOutside}>
      {/* Header - white background extends to right edge */}
      <div className="shrink-0 bg-white border-b border-surface-border">
        <div className="px-8 py-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-medium text-gray-900 tracking-tight">
              Templates
            </h1>
            <p className="text-base text-gray-500 mt-1">
              Manage AI templates, versions, and configurations
            </p>
          </div>
          <Link
            to="/admin/templates/new"
            className="h-10 px-4 flex items-center gap-2 bg-brand-700 rounded-lg text-white hover:bg-brand-800 transition-colors"
          >
            <Plus className="w-5 h-5" />
            <span>Create New Template</span>
          </Link>
        </div>

        {/* Search and Filters - Matching Figma design */}
        <div className="flex items-center gap-3">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search templates..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full h-[42px] pl-10 pr-4 border border-surface-border rounded-lg text-base text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500"
            />
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={cn(
              'flex items-center gap-2 h-[42px] px-4 border rounded-lg transition-colors',
              showFilters
                ? 'bg-brand-100 border-brand-200 text-brand-800'
                : 'border-surface-border text-gray-700 hover:bg-surface-secondary'
            )}
          >
            <SlidersHorizontal className="w-5 h-5" />
            Filters
          </button>
          
          {/* Show Archived Toggle */}
          <label className="flex items-center gap-2 h-[42px] px-4 border border-surface-border rounded-lg cursor-pointer hover:bg-surface-secondary transition-colors">
            <div className="relative">
              <input
                type="checkbox"
                checked={showArchived}
                onChange={(e) => setShowArchived(e.target.checked)}
                className="sr-only peer"
              />
              <div className={cn(
                "w-9 h-5 rounded-full transition-colors",
                showArchived ? "bg-brand-700" : "bg-gray-300"
              )} />
              <div className={cn(
                "absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform",
                showArchived && "translate-x-4"
              )} />
            </div>
            <span className="text-sm text-gray-700 whitespace-nowrap">Show Archived</span>
          </label>
        </div>

        {/* Filter Dropdowns - Toggle visibility */}
        {showFilters && (
          <div className="mt-4 flex items-center gap-3">
            <Select value={statusFilter || '__all__'} onValueChange={(v) => setStatusFilter(v === '__all__' ? '' : v)}>
              <SelectTrigger className="h-[42px] w-[180px] border-surface-border rounded-lg text-gray-700 focus:ring-brand-500/20 focus:border-brand-500">
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent className="rounded-lg border-surface-border shadow-dropdown">
                <SelectItem value="__all__">All Statuses</SelectItem>
                <SelectItem value="ACTIVE">Active</SelectItem>
                <SelectItem value="DRAFT">Draft</SelectItem>
                <SelectItem value="IN_REVIEW">In Review</SelectItem>
                <SelectItem value="DEPRECATED">Deprecated</SelectItem>
              </SelectContent>
            </Select>
            <Select value={categoryFilter || '__all__'} onValueChange={(v) => setCategoryFilter(v === '__all__' ? '' : v)}>
              <SelectTrigger className="h-[42px] w-[180px] border-surface-border rounded-lg text-gray-700 focus:ring-brand-500/20 focus:border-brand-500">
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent className="rounded-lg border-surface-border shadow-dropdown">
                <SelectItem value="__all__">All Categories</SelectItem>
                <SelectItem value="project">Project</SelectItem>
                <SelectItem value="discovery">Discovery</SelectItem>
                <SelectItem value="definition">Definition</SelectItem>
                <SelectItem value="design">Design</SelectItem>
                <SelectItem value="delivery">Delivery</SelectItem>
              </SelectContent>
            </Select>
            <Select value={typeFilter || '__all__'} onValueChange={(v) => setTypeFilter(v === '__all__' ? '' : v)}>
              <SelectTrigger className="h-[42px] w-[200px] border-surface-border rounded-lg text-gray-700 focus:ring-brand-500/20 focus:border-brand-500">
                <SelectValue placeholder="All Types" />
              </SelectTrigger>
              <SelectContent className="rounded-lg border-surface-border shadow-dropdown max-h-[300px]">
                <SelectItem value="__all__">All Types</SelectItem>
                {Object.entries(TEMPLATE_TYPE_LABELS).map(([value, label]) => (
                  <SelectItem key={value} value={value}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {(statusFilter || categoryFilter || typeFilter) && (
              <button
                onClick={() => {
                  setStatusFilter('');
                  setCategoryFilter('');
                  setTypeFilter('');
                }}
                className="text-sm text-gray-500 hover:text-gray-700"
              >
                Clear filters
              </button>
            )}
          </div>
        )}
        </div>
      </div>

      {/* Content - gray background extends to right edge */}
      <div className="flex-1 overflow-auto bg-surface-secondary">
        <div className="container px-8 py-6">
        {isLoading ? (
          <div className="bg-white border border-surface-border rounded-lg overflow-hidden">
            <div className="p-4 space-y-4">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="flex items-center gap-4 p-4 border-b border-gray-100 last:border-0">
                  <Skeleton className="h-5 w-1/4" />
                  <Skeleton className="h-5 w-32" />
                  <Skeleton className="h-5 w-16" />
                  <Skeleton className="h-5 w-16" />
                  <Skeleton className="h-5 w-20" />
                  <Skeleton className="h-5 w-24" />
                </div>
              ))}
            </div>
          </div>
        ) : isError ? (
          <div className="bg-white border border-surface-border rounded-lg p-8 text-center">
            <p className="text-red-600">Failed to load templates. Please try again.</p>
            <button
              onClick={() => refetch()}
              className="mt-4 text-brand-700 hover:underline"
            >
              Retry
            </button>
          </div>
        ) : templates.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full bg-white border-2 border-dashed border-gray-300 rounded-lg py-16">
            <FileText className="w-16 h-16 text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No templates found</h3>
            <p className="text-gray-500 mb-6">
              {searchQuery || categoryFilter || statusFilter
                ? 'Try adjusting your search or filters'
                : 'Get started by creating your first template'}
            </p>
            {!searchQuery && !categoryFilter && !statusFilter && (
              <Link
                to="/admin/templates/new"
                className="inline-flex items-center gap-2 h-10 px-4 bg-brand-700 text-white rounded-lg hover:bg-brand-800"
              >
                <Plus className="w-5 h-5" />
                Create New Template
              </Link>
            )}
          </div>
        ) : (
          <div className="bg-white border border-surface-border rounded-lg overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th 
                    onClick={() => handleSort('name')}
                    className="px-6 py-3 text-left text-gray-700 cursor-pointer hover:bg-gray-100 transition-colors select-none"
                  >
                    <div className="flex items-center gap-2 text-sm font-medium">
                      Template Name
                      <span className="text-gray-400">{getSortIcon('name')}</span>
                    </div>
                  </th>
                  <th 
                    onClick={() => handleSort('type')}
                    className="px-6 py-3 text-left text-gray-700 cursor-pointer hover:bg-gray-100 transition-colors select-none"
                  >
                    <div className="flex items-center gap-2 text-sm font-medium">
                      Type / Category
                      <span className="text-gray-400">{getSortIcon('type')}</span>
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left">
                    <span className="text-sm font-medium text-gray-700">Version</span>
                  </th>
                  <th 
                    onClick={() => handleSort('status')}
                    className="px-6 py-3 text-left text-gray-700 cursor-pointer hover:bg-gray-100 transition-colors select-none"
                  >
                    <div className="flex items-center gap-2 text-sm font-medium">
                      Status
                      <span className="text-gray-400">{getSortIcon('status')}</span>
                    </div>
                  </th>
                  <th 
                    onClick={() => handleSort('lastUpdated')}
                    className="px-6 py-3 text-left text-gray-700 cursor-pointer hover:bg-gray-100 transition-colors select-none"
                  >
                    <div className="flex items-center gap-2 text-sm font-medium">
                      Last Updated
                      <span className="text-gray-400">{getSortIcon('lastUpdated')}</span>
                    </div>
                  </th>
                  <th 
                    onClick={() => handleSort('owner')}
                    className="px-6 py-3 text-left text-gray-700 cursor-pointer hover:bg-gray-100 transition-colors select-none"
                  >
                    <div className="flex items-center gap-2 text-sm font-medium">
                      Owner
                      <span className="text-gray-400">{getSortIcon('owner')}</span>
                    </div>
                  </th>
                  <th className="px-6 py-3 text-right">
                    <span className="text-sm font-medium text-gray-700">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {templates.map((template) => (
                  <tr
                    key={template.id}
                    data-template-id={template.id}
                    onClick={() => handleRowClick(template.id)}
                    className="hover:bg-gray-50 cursor-pointer transition-all duration-300"
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <TemplateIcon 
                          icon={template.icon} 
                          category={template.category}
                          size="sm"
                        />
                        <div className="flex items-center gap-2">
                          {template.isDefault && (
                            <Star className="w-4 h-4 text-amber-500 fill-amber-500" />
                          )}
                          {template.isArchived && (
                            <Archive className="w-4 h-4 text-gray-400" />
                          )}
                          <span className={cn("text-gray-900 font-medium", template.isArchived && "text-gray-500 italic")}>
                            {template.name}
                          </span>
                          {template.isArchived && (
                            <span className="text-xs px-1.5 py-0.5 bg-gray-100 text-gray-500 rounded">Archived</span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-col gap-1">
                        <span className="text-gray-900">
                          {TEMPLATE_TYPE_LABELS[template.type as TemplateType] || template.type}
                        </span>
                        <span className="text-gray-500 text-sm">
                          {TEMPLATE_CATEGORY_LABELS[template.category as TemplateCategory] || template.category}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="px-2.5 py-1 bg-gray-100 text-gray-700 rounded text-xs font-mono">
                        v{template.activeVersion}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <StatusBadge status={template.status} />
                    </td>
                    <td className="px-6 py-4 text-gray-700">
                      {new Date(template.updatedAt).toLocaleDateString('en-CA')}
                    </td>
                    <td className="px-6 py-4 text-gray-700">
                      {template.owner?.name || template.owner?.email || 'Unknown'}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-2" onClick={(e) => e.stopPropagation()}>
                        <button
                          onClick={() => setHistoryModal({ open: true, template })}
                          className="p-1.5 text-gray-600 hover:bg-gray-200 rounded transition-colors"
                          title="Version History"
                        >
                          <GitBranch className="w-4 h-4" />
                        </button>
                        <div className="relative">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              if (openMenuId === template.id) {
                                setOpenMenuId(null);
                              } else {
                                // Calculate menu position using fixed coordinates
                                const button = e.currentTarget;
                                const rect = button.getBoundingClientRect();
                                const menuWidth = 208; // w-52 = 13rem = 208px
                                const menuHeight = 320; // Approximate menu height
                                const spaceBelow = window.innerHeight - rect.bottom;
                                
                                // Position: right-aligned to button, below or above based on space
                                const left = Math.max(8, rect.right - menuWidth);
                                const top = spaceBelow < menuHeight 
                                  ? rect.top - menuHeight - 8 
                                  : rect.bottom + 8;
                                
                                setMenuPosition(spaceBelow < menuHeight ? 'above' : 'below');
                                setMenuCoords({ top, left });
                                setOpenMenuId(template.id);
                              }
                            }}
                            className="p-1.5 text-gray-600 hover:bg-gray-200 rounded transition-colors"
                            title="More actions"
                          >
                            <MoreVertical className="w-4 h-4" />
                          </button>
                          {openMenuId === template.id && (
                            <>
                              <div 
                                className="fixed inset-0 z-40"
                                onClick={() => setOpenMenuId(null)}
                              />
                              <div 
                                className="fixed w-52 bg-white border border-surface-border rounded-xl shadow-dropdown z-50"
                                style={{ top: menuCoords.top, left: menuCoords.left }}
                              >
                                <ActionHandlers template={template} />
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <Dialog open={deleteModal.open} onOpenChange={(open) => !open && setDeleteModal({ open: false, template: null })}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-full">
                <Archive className="w-5 h-5 text-amber-600" />
              </div>
              Archive Template
            </DialogTitle>
            <DialogDescription className="pt-3">
              Are you sure you want to archive <span className="font-medium text-gray-900">"{deleteModal.template?.name}"</span>?
              <br /><br />
              <span className="text-gray-500">The template will be moved to archive and can be restored later. Enable "Show Archived" to see archived templates.</span>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setDeleteModal({ open: false, template: null })}
            >
              Cancel
            </Button>
            <Button
              onClick={confirmDelete}
              disabled={deleteTemplate.isPending}
              className="bg-amber-600 hover:bg-amber-700 text-white"
            >
              {deleteTemplate.isPending ? 'Archiving...' : 'Archive Template'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Deprecate Confirmation Modal */}
      <Dialog open={deprecateModal.open} onOpenChange={(open) => !open && setDeprecateModal({ open: false, template: null })}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 rounded-full">
                <Archive className="w-5 h-5 text-orange-600" />
              </div>
              Deprecate Template
            </DialogTitle>
            <DialogDescription className="pt-3">
              Are you sure you want to deprecate <span className="font-medium text-gray-900">"{deprecateModal.template?.name}"</span>?
              <br /><br />
              <span className="text-gray-500">Deprecated templates cannot be used for new artifacts but will remain in history for existing projects.</span>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setDeprecateModal({ open: false, template: null })}
            >
              Cancel
            </Button>
            <DeprecateButton templateId={deprecateModal.template?.id} onConfirm={confirmDeprecate} />
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Version History Modal */}
      <VersionHistoryModal
        isOpen={historyModal.open}
        onClose={() => setHistoryModal({ open: false, template: null })}
        templateId={historyModal.template?.id || ''}
        templateName={historyModal.template?.name || ''}
      />
    </div>
  );
}

// Separate component for deprecate button to use the hook
function DeprecateButton({ 
  templateId, 
  onConfirm 
}: { 
  templateId: string | undefined; 
  onConfirm: (mutation: ReturnType<typeof useDeprecateTemplate>) => void;
}) {
  const deprecateTemplate = useDeprecateTemplate(templateId || '');
  
  if (!templateId) return null;
  
  return (
    <Button
      onClick={() => onConfirm(deprecateTemplate)}
      disabled={deprecateTemplate.isPending}
      className="bg-orange-600 hover:bg-orange-700"
    >
      {deprecateTemplate.isPending ? 'Deprecating...' : 'Deprecate Template'}
    </Button>
  );
}
